import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { integrationsService } from '../../services';

// Async thunks for API calls
export const fetchIntegrations = createAsyncThunk(
  'integrations/fetchIntegrations',
  async (_, { rejectWithValue }) => {
    try {
      const data = await integrationsService.list();
      return data.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateIntegration = createAsyncThunk(
  'integrations/updateIntegration',
  async ({ type, config }, { rejectWithValue }) => {
    try {
      const data = await integrationsService.update(type, config);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchMCPServers = createAsyncThunk(
  'integrations/fetchMCPServers',
  async (_, { rejectWithValue }) => {
    try {
      const data = await integrationsService.mcp.list();
      return data.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const addMCPServer = createAsyncThunk(
  'integrations/addMCPServer',
  async (serverData, { rejectWithValue }) => {
    try {
      const data = await integrationsService.mcp.add(serverData);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateMCPServer = createAsyncThunk(
  'integrations/updateMCPServer',
  async ({ serverId, serverData }, { rejectWithValue }) => {
    try {
      const data = await integrationsService.mcp.update(serverId, serverData);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteMCPServer = createAsyncThunk(
  'integrations/deleteMCPServer',
  async (serverId, { rejectWithValue }) => {
    try {
      await integrationsService.mcp.remove(serverId);
      return serverId;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchZapierWebhooks = createAsyncThunk(
  'integrations/fetchZapierWebhooks',
  async (_, { rejectWithValue }) => {
    try {
      const data = await integrationsService.zapier.webhooks.list();
      return data.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const addZapierWebhook = createAsyncThunk(
  'integrations/addZapierWebhook',
  async (webhookData, { rejectWithValue }) => {
    try {
      const data = await integrationsService.zapier.webhooks.add(webhookData);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateZapierWebhook = createAsyncThunk(
  'integrations/updateZapierWebhook',
  async ({ webhookId, webhookData }, { rejectWithValue }) => {
    try {
      const data = await integrationsService.zapier.webhooks.update(webhookId, webhookData);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteZapierWebhook = createAsyncThunk(
  'integrations/deleteZapierWebhook',
  async (webhookId, { rejectWithValue }) => {
    try {
      await integrationsService.zapier.webhooks.remove(webhookId);
      return webhookId;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// VOIP Integrations (Twilio/Exotel)
export const fetchVoipIntegrations = createAsyncThunk(
  'integrations/fetchVoipIntegrations',
  async (_, { rejectWithValue }) => {
    try {
      const data = await integrationsService.voip.list();
      return data.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const saveVoipIntegration = createAsyncThunk(
  'integrations/saveVoipIntegration',
  async ({ provider, config }, { rejectWithValue }) => {
    try {
      const data = await integrationsService.voip.update(provider, config);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const testVoipConnection = createAsyncThunk(
  'integrations/testVoipConnection',
  async ({ provider, config }, { rejectWithValue }) => {
    try {
      const data = await integrationsService.voip.test(provider);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  // General Integrations
  integrations: [],
  integrationsLoading: false,
  integrationsError: null,

  // MCP Servers
  mcpServers: [],
  mcpServersLoading: false,
  mcpServersError: null,

  // Zapier Webhooks
  zapierWebhooks: [],
  zapierWebhooksLoading: false,
  zapierWebhooksError: null,

  // VOIP Integrations
  voipIntegrations: {
    twilio: null,
    exotel: null,
  },
  voipLoading: false,
  voipError: null,
  voipTestResult: null,
};

const integrationsSlice = createSlice({
  name: 'integrations',
  initialState,
  reducers: {
    clearError: state => {
      state.integrationsError = null;
      state.mcpServersError = null;
      state.zapierWebhooksError = null;
      state.voipError = null;
    },
    clearVoipTestResult: state => {
      state.voipTestResult = null;
    },
  },
  extraReducers: builder => {
    builder
      // Fetch integrations
      .addCase(fetchIntegrations.pending, state => {
        state.integrationsLoading = true;
        state.integrationsError = null;
      })
      .addCase(fetchIntegrations.fulfilled, (state, action) => {
        state.integrationsLoading = false;
        state.integrations = action.payload;
      })
      .addCase(fetchIntegrations.rejected, (state, action) => {
        state.integrationsLoading = false;
        state.integrationsError = action.payload;
      })
      // Update integration
      .addCase(updateIntegration.pending, state => {
        state.integrationsLoading = true;
        state.integrationsError = null;
      })
      .addCase(updateIntegration.fulfilled, (state, action) => {
        state.integrationsLoading = false;
        const index = state.integrations.findIndex(
          integration => integration.type === action.payload.type
        );
        if (index !== -1) {
          state.integrations[index] = action.payload;
        } else {
          state.integrations.push(action.payload);
        }
      })
      .addCase(updateIntegration.rejected, (state, action) => {
        state.integrationsLoading = false;
        state.integrationsError = action.payload;
      })
      // Fetch MCP servers
      .addCase(fetchMCPServers.pending, state => {
        state.mcpServersLoading = true;
        state.mcpServersError = null;
      })
      .addCase(fetchMCPServers.fulfilled, (state, action) => {
        state.mcpServersLoading = false;
        state.mcpServers = action.payload;
      })
      .addCase(fetchMCPServers.rejected, (state, action) => {
        state.mcpServersLoading = false;
        state.mcpServersError = action.payload;
      })
      // Add MCP server
      .addCase(addMCPServer.pending, state => {
        state.mcpServersLoading = true;
        state.mcpServersError = null;
      })
      .addCase(addMCPServer.fulfilled, (state, action) => {
        state.mcpServersLoading = false;
        state.mcpServers.push(action.payload);
      })
      .addCase(addMCPServer.rejected, (state, action) => {
        state.mcpServersLoading = false;
        state.mcpServersError = action.payload;
      })
      // Update MCP server
      .addCase(updateMCPServer.pending, state => {
        state.mcpServersLoading = true;
        state.mcpServersError = null;
      })
      .addCase(updateMCPServer.fulfilled, (state, action) => {
        state.mcpServersLoading = false;
        const index = state.mcpServers.findIndex(server => server.id === action.payload.id);
        if (index !== -1) {
          state.mcpServers[index] = action.payload;
        }
      })
      .addCase(updateMCPServer.rejected, (state, action) => {
        state.mcpServersLoading = false;
        state.mcpServersError = action.payload;
      })
      // Delete MCP server
      .addCase(deleteMCPServer.pending, state => {
        state.mcpServersLoading = true;
        state.mcpServersError = null;
      })
      .addCase(deleteMCPServer.fulfilled, (state, action) => {
        state.mcpServersLoading = false;
        state.mcpServers = state.mcpServers.filter(server => server.id !== action.payload);
      })
      .addCase(deleteMCPServer.rejected, (state, action) => {
        state.mcpServersLoading = false;
        state.mcpServersError = action.payload;
      })
      // Fetch Zapier webhooks
      .addCase(fetchZapierWebhooks.pending, state => {
        state.zapierWebhooksLoading = true;
        state.zapierWebhooksError = null;
      })
      .addCase(fetchZapierWebhooks.fulfilled, (state, action) => {
        state.zapierWebhooksLoading = false;
        state.zapierWebhooks = action.payload;
      })
      .addCase(fetchZapierWebhooks.rejected, (state, action) => {
        state.zapierWebhooksLoading = false;
        state.zapierWebhooksError = action.payload;
      })
      // Add Zapier webhook
      .addCase(addZapierWebhook.pending, state => {
        state.zapierWebhooksLoading = true;
        state.zapierWebhooksError = null;
      })
      .addCase(addZapierWebhook.fulfilled, (state, action) => {
        state.zapierWebhooksLoading = false;
        state.zapierWebhooks.push(action.payload);
      })
      .addCase(addZapierWebhook.rejected, (state, action) => {
        state.zapierWebhooksLoading = false;
        state.zapierWebhooksError = action.payload;
      })
      // Update Zapier webhook
      .addCase(updateZapierWebhook.pending, state => {
        state.zapierWebhooksLoading = true;
        state.zapierWebhooksError = null;
      })
      .addCase(updateZapierWebhook.fulfilled, (state, action) => {
        state.zapierWebhooksLoading = false;
        const index = state.zapierWebhooks.findIndex(webhook => webhook.id === action.payload.id);
        if (index !== -1) {
          state.zapierWebhooks[index] = action.payload;
        }
      })
      .addCase(updateZapierWebhook.rejected, (state, action) => {
        state.zapierWebhooksLoading = false;
        state.zapierWebhooksError = action.payload;
      })
      // Delete Zapier webhook
      .addCase(deleteZapierWebhook.pending, state => {
        state.zapierWebhooksLoading = true;
        state.zapierWebhooksError = null;
      })
      .addCase(deleteZapierWebhook.fulfilled, (state, action) => {
        state.zapierWebhooksLoading = false;
        state.zapierWebhooks = state.zapierWebhooks.filter(
          webhook => webhook.id !== action.payload
        );
      })
      .addCase(deleteZapierWebhook.rejected, (state, action) => {
        state.zapierWebhooksLoading = false;
        state.zapierWebhooksError = action.payload;
      })
      // Fetch VOIP integrations
      .addCase(fetchVoipIntegrations.pending, state => {
        state.voipLoading = true;
        state.voipError = null;
      })
      .addCase(fetchVoipIntegrations.fulfilled, (state, action) => {
        state.voipLoading = false;
        state.voipIntegrations = action.payload;
      })
      .addCase(fetchVoipIntegrations.rejected, (state, action) => {
        state.voipLoading = false;
        state.voipError = action.payload;
      })
      // Save VOIP integration
      .addCase(saveVoipIntegration.pending, state => {
        state.voipLoading = true;
        state.voipError = null;
      })
      .addCase(saveVoipIntegration.fulfilled, (state, action) => {
        state.voipLoading = false;
        state.voipIntegrations[action.payload.provider] = action.payload;
      })
      .addCase(saveVoipIntegration.rejected, (state, action) => {
        state.voipLoading = false;
        state.voipError = action.payload;
      })
      // Test VOIP connection
      .addCase(testVoipConnection.pending, state => {
        state.voipLoading = true;
        state.voipError = null;
        state.voipTestResult = null;
      })
      .addCase(testVoipConnection.fulfilled, (state, action) => {
        state.voipLoading = false;
        state.voipTestResult = action.payload;
      })
      .addCase(testVoipConnection.rejected, (state, action) => {
        state.voipLoading = false;
        state.voipError = action.payload;
      });
  },
});

export const { clearError, clearVoipTestResult } = integrationsSlice.actions;

// Selectors
export const selectMCPServers = state => state.integrations.mcpServers;
export const selectZapierWebhooks = state => state.integrations.zapierWebhooks;
export const selectVoipIntegrations = state => state.integrations.voipIntegrations;

// Loading selectors
export const selectIntegrationsLoading = state => state.integrations.integrationsLoading;
export const selectMCPServersLoading = state => state.integrations.mcpServersLoading;
export const selectZapierWebhooksLoading = state => state.integrations.zapierWebhooksLoading;
export const selectVoipLoading = state => state.integrations.voipLoading;

// Error selectors
export const selectIntegrationsError = state => state.integrations.integrationsError;
export const selectMCPServersError = state => state.integrations.mcpServersError;
export const selectZapierWebhooksError = state => state.integrations.zapierWebhooksError;
export const selectVoipError = state => state.integrations.voipError;

// VOIP specific selectors
export const selectVoipTestResult = state => state.integrations.voipTestResult;

export default integrationsSlice.reducer;
